import { useState, FormEvent } from 'react';
import { useCart } from '../../../hooks/useCart';
import { useCheckout } from '../../../hooks/useCheckout';
import { useAuth } from '../../../context/AuthContext';
import { getCheckoutUrlForLoggedInUser } from '../../../lib/store/checkout';
import { formatPriceFromGrosz } from '../../../utils/price';

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

export default function CheckoutModal({ isOpen, onClose }: Props) {
    const { data: cart, isLoading: isCartLoading } = useCart();
    const { isLoggedIn, isCheckingAuth, login, logout, authError } = useAuth();

    // Gość -> "Kontynuuj jako gość" korzysta z istniejącej logiki
    // (walidacja koszyka + URL z cocart-load-cart).
    const { goToCheckout: continueAsGuest, isLoading: isGuestCheckoutLoading, error: guestCheckoutError } = useCheckout();

    const [showLoginForm, setShowLoginForm] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const resetPasswordUrl = "https://hardshop.hard-adv.com/index.php/moje-konto/lost-password/";
    const handleLoginSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoggingIn(true);
        const success = await login(username, password);
        setIsLoggingIn(false);
        if (success) {
            setShowLoginForm(false);
            setUsername('');
            setPassword('');
        }
    };

    const handleContinueLoggedIn = () => {
        setIsRedirecting(true);
        window.location.href = getCheckoutUrlForLoggedInUser();
    };

    if (!isOpen) return null;

    const items = cart?.items || [];
    const hasItems = items.length > 0;
    const subtotalInGrosz = parseFloat(cart?.totals?.subtotal || '0');

    return (
        <div className="store-modal__overlay" onClick={onClose}>
            <div className="store-modal store-modal--checkout" onClick={(e) => e.stopPropagation()}>
                <div className="store-modal__header">
                    <h2 className="store-modal__title">Podsumowanie przed kasą</h2>
                    <button className="store-modal__close" onClick={onClose} aria-label="Zamknij">
                        ✕
                    </button>
                </div>

                <div className="store-modal__checkout-layout">
                    <div className="store-modal__checkout-summary">
                        <h3 className="store-modal__section-title">Podsumowanie</h3>

                        {isCartLoading ? (
                            <p>Ładowanie koszyka...</p>
                        ) : !hasItems ? (
                            <p className="store-modal__empty">Twój koszyk jest pusty.</p>
                        ) : (
                            <table className="store-modal__summary-table">
                                <thead>
                                    <tr>
                                        <th>Produkt</th>
                                        <th>Ilość</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item: any) => (
                                        <tr key={item.item_key}>
                                            <td>{item.name}</td>
                                            <td className="store-modal__summary-qty">
                                                × {item.quantity?.value ?? item.quantity ?? 1}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Razem (produkty)</td>
                                        <td>{formatPriceFromGrosz(subtotalInGrosz)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}

                        <p className="store-modal__payment-info">
                            Podana kwota nie zawiera kosztów wysyłki — zostaną one doliczone w kolejnym kroku,
                            podczas składania zamówienia na stronie kasy.
                        </p>
                    </div>

                    <div className="store-modal__checkout-form">
                        <h3 className="store-modal__section-title">Dane do zamówienia</h3>

                        {isCheckingAuth ? (
                            <p>Sprawdzanie stanu logowania...</p>
                        ) : isLoggedIn ? (
                            <>
                                <p>Jesteś zalogowany — Twoje zamówienie zostanie powiązane z kontem.</p>
                                <div className="store-modal__cart-actions">
                                    <button className="store-modal__action-btn" onClick={() => logout()}>
                                        Wyloguj
                                    </button>
                                    <button
                                        className="store-modal__action-btn store-modal__action-btn--primary"
                                        onClick={handleContinueLoggedIn}
                                        disabled={!hasItems || isRedirecting}
                                    >
                                        {isRedirecting ? 'Przekierowywanie...' : 'Kontynuuj'}
                                    </button>
                                </div>
                            </>
                        ) : showLoginForm ? (
                            <form className="store-modal__form-grid" onSubmit={handleLoginSubmit}>
                                <input
                                    className="store-modal__input store-modal__input--full"
                                    type="text"
                                    placeholder="Login lub e-mail"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                                <input
                                    className="store-modal__input store-modal__input--full"
                                    type="password"
                                    placeholder="Hasło"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                {authError && (
                                  <>
                                    <p className="store-modal__form-error">{authError} Jeśli zapomniałeś hasło i chcesz je odzyskać skorzystaj z formularza
                                      <a href={resetPasswordUrl} className="store-modal__form-link"> odzyskiwania hasła</a>
                                    </p>
                                  </>
                                )}                                <div className="store-modal__cart-actions store-modal__input--full">
                                    <button
                                        type="button"
                                        className="store-modal__action-btn"
                                        onClick={() => setShowLoginForm(false)}
                                    >
                                        Anuluj
                                    </button>
                                    <button
                                        type="submit"
                                        className="store-modal__action-btn store-modal__action-btn--primary"
                                        aria-disabled={isLoggingIn}
                                    >
                                        {isLoggingIn ? 'Logowanie...' : 'Zaloguj się'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <>
                                <p>Możesz złożyć zamówienie jako gość albo zalogować się na swoje konto.</p>
                                <div className="store-modal__cart-actions">
                                    <button
                                        className="store-modal__action-btn"
                                        onClick={continueAsGuest}
                                        disabled={!hasItems || isGuestCheckoutLoading}
                                    >
                                        {isGuestCheckoutLoading ? 'Przetwarzanie...' : 'Kontynuuj jako gość'}
                                    </button>
                                    <button
                                        className="store-modal__action-btn store-modal__action-btn--primary"
                                        onClick={() => setShowLoginForm(true)}
                                    >
                                        Zaloguj się
                                    </button>
                                </div>
                        {guestCheckoutError && <p className="store-modal__form-error">{guestCheckoutError}</p>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
